import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState, useEffect } from 'react'
import BaseStep from './BaseStep'
import { useProductModalContext } from './ProductModalContext'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { getSingularUnit } from 'utils/reportingUnits'
import * as S from './styles'

export function ProductDetailsStep({
  onNext,
  totalSteps = 2,
}: {
  onNext: () => void
  totalSteps?: number
}) {
  const {
    methods: { handleClose, productForm },
    state: { selectedProductID },
  } = useProductModalContext()
  const [isBrandExpanded, setIsBrandExpanded] = useState(false)
  const { preferences, isLoading } = usePreferences()

  const { control, formState, watch, trigger } = productForm

  const productName = watch('name')
  const reportingUnit = preferences?.reporting_unit

  // Trigger validation on mount for create mode
  useEffect(() => {
    if (!selectedProductID) {
      trigger()
    }
  }, [selectedProductID, trigger])

  // Show loading UI while preferences are loading
  if (isLoading) {
    return (
      <BaseStep
        handleClose={handleClose}
        onContinue={onNext}
        title={selectedProductID ? 'Update Product' : 'Create Product'}
        description="Loading preferences..."
        continueButtonText="Next"
        cancelButtonText="Cancel"
        currentStep={1}
        totalSteps={totalSteps}
        continueDisabled={true}
      >
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </BaseStep>
    )
  }

  // Check if there are actual errors instead of relying on isValid
  const hasErrors = Object.keys(formState.errors).length > 0

  return (
    <BaseStep
      handleClose={handleClose}
      onContinue={onNext}
      title={selectedProductID ? 'Update Product' : 'Create Product'}
      description="To get started, add preliminary sales estimates. You can always adjust these later."
      continueButtonText="Next"
      cancelButtonText="Cancel"
      currentStep={1}
      totalSteps={totalSteps}
      continueDisabled={hasErrors}
    >
      <div className="space-y-4">
        <S.Card>
          <h3 className="text-sm font-medium text-gray-900 mb-4">
            Default product sales
          </h3>
          <div className="space-y-4">
            <TextInput
              control={control}
              name="name"
              type="text"
              label={
                `Name` + (productName ? ` (${productName.length}/60)` : '')
              }
              placeholder="Name"
            />

            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label
                    htmlFor="mfg_avg_list_price_per_lb"
                    className="text-sm font-medium text-gray-700"
                  >
                    Avg. price per{' '}
                    {reportingUnit ? getSingularUnit(reportingUnit) : 'unit'}
                  </label>
                  {preferences?.tooltips?.products_avg_revenue_per_unit && (
                    <HelpTooltip
                      title={
                        preferences.tooltips.products_avg_revenue_per_unit.title
                      }
                      content={
                        preferences.tooltips.products_avg_revenue_per_unit
                          .content
                      }
                    />
                  )}
                </div>
                <TextInput
                  min={0}
                  name="mfg_avg_list_price_per_lb"
                  control={control}
                  type="number"
                  placeholder={`Avg. price per ${
                    reportingUnit ? getSingularUnit(reportingUnit) : 'unit'
                  }`}
                  prefix="$"
                  suffix={`/ ${
                    reportingUnit ? getSingularUnit(reportingUnit) : 'unit'
                  }`}
                  allowDecimals
                  noMargin
                />
              </div>

              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label
                    htmlFor="est_monthly_churn_rate"
                    className="text-sm font-medium text-gray-700"
                  >
                    Est. monthly churn rate
                  </label>
                  {preferences?.tooltips?.products_est_monthly_churn_rate && (
                    <HelpTooltip
                      title={
                        preferences.tooltips.products_est_monthly_churn_rate
                          .title
                      }
                      content={
                        preferences.tooltips.products_est_monthly_churn_rate
                          .content
                      }
                    />
                  )}
                </div>
                <TextInput
                  min={1}
                  max={100}
                  name="est_monthly_churn_rate"
                  control={control}
                  type="number"
                  placeholder="Est. monthly churn rate"
                  suffix="% / month"
                  allowDecimals
                  noMargin
                />
              </div>
            </div>
          </div>
        </S.Card>

        <S.Card>
          <button
            onClick={() => setIsBrandExpanded(!isBrandExpanded)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-900">Brand value</h3>
              <S.Badge>Optional</S.Badge>
            </div>
            {isBrandExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {isBrandExpanded && (
            <>
              <div className="text-sm text-gray-600 mt-2 mb-4">
                You may consider some opportunities more valuable based on their
                reputation. Add details here to see brand values calculated and
                included in total value calculations.
              </div>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                <div>
                  <TextInput
                    min={0}
                    name="value_per_consumer_new_trial"
                    control={control}
                    type="number"
                    label="Value"
                    placeholder="Value"
                    prefix="$"
                    suffix={`/ ${
                      reportingUnit ? getSingularUnit(reportingUnit) : 'unit'
                    }`}
                    allowDecimals
                  />
                  <p className="text-gray-600 text-sm ml-2 mt-2">
                    Per new informed trial participant
                  </p>
                </div>

                <TextInput
                  min={0}
                  max={100}
                  name="current_aided_awareness"
                  control={control}
                  type="number"
                  label="Existing aided awareness"
                  placeholder="Existing aided awareness"
                  suffix="%"
                  allowDecimals
                />

                <TextInput
                  min={0}
                  max={100}
                  name="current_national_trial_perc"
                  control={control}
                  type="number"
                  label="Existing national trial"
                  placeholder="Existing national trial"
                  suffix="%"
                  allowDecimals
                />
              </div>
            </>
          )}
        </S.Card>
      </div>
    </BaseStep>
  )
}
